import { ChevronDown } from "lucide-react";
import {
  Button as AriaButton,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
  Text,
  type Key,
} from "react-aria-components";
import { cx } from "../cx.js";
import styles from "./Select.module.css";

export interface SelectOption {
  id: string;
  label: string;
}

export interface SelectProps {
  label: string;
  options: SelectOption[];
  value: string | null;
  onChange: (id: string) => void;
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  className?: string;
}

export function Select({
  label,
  options,
  value,
  onChange,
  placeholder,
  description,
  errorMessage,
  isRequired,
  isDisabled,
  className,
}: SelectProps) {
  return (
    <AriaSelect
      className={cx(styles.field, className)}
      selectedKey={value}
      onSelectionChange={(k: Key | null) => k !== null && onChange(String(k))}
      placeholder={placeholder}
      isInvalid={!!errorMessage}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Label className={styles.label}>{label}</Label>
      <AriaButton className={styles.trigger}>
        <SelectValue className={styles.value} />
        <ChevronDown className={styles.chevron} aria-hidden="true" />
      </AriaButton>
      {description ? (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      ) : null}
      <FieldError className={styles.error}>{errorMessage}</FieldError>
      <Popover className={styles.popover} offset={6}>
        <ListBox className={styles.list} items={options}>
          {(o) => (
            <ListBoxItem id={o.id} className={styles.item} textValue={o.label}>
              {o.label}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
